import React, { useState } from 'react';
import { 
  Building2, Users, Package, Receipt, Clock, Wifi, WifiOff, 
  CheckCircle2, XCircle, ArrowRight, Phone, Mail, Calendar,
  BarChart3, CreditCard, Truck, UserCheck, FileText, Smartphone,
  Shield, Zap, TrendingUp, Store, Coffee, UtensilsCrossed, 
  ShoppingBag, Stethoscope, ChevronDown, ChevronUp, Play,
  MessageCircle, Star, Award, Globe
} from 'lucide-react';

const FOSLandingPage = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showDemoForm, setShowDemoForm] = useState(false);

  const painPoints = [
    { icon: <XCircle className="w-5 h-5" />, text: "No real-time visibility of franchise sales" },
    { icon: <XCircle className="w-5 h-5" />, text: "Daily reports coming through WhatsApp & Excel" },
    { icon: <XCircle className="w-5 h-5" />, text: "Credit customers and ledger disputes" },
    { icon: <XCircle className="w-5 h-5" />, text: "No control over staff attendance" },
    { icon: <XCircle className="w-5 h-5" />, text: "Raw material orders tracked manually" },
    { icon: <XCircle className="w-5 h-5" />, text: "Every outlet operating differently" },
  ];

  const features = [
    {
      icon: <Receipt className="w-8 h-8" />,
      title: "Franchise Billing & POS",
      description: "Fast billing with cash and ledger transactions. Thermal printer & PDF invoice support.",
      result: "Faster checkout, accurate accounts.",
      color: "from-amber-500 to-orange-500"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "HQ Dashboard",
      description: "View sales and performance of all franchises in one place. Compare outlets instantly.",
      result: "No blind spots. No surprises.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <CreditCard className="w-8 h-8" />,
      title: "Ledger & Credit Management",
      description: "Party-wise balances with automatic debit & credit tracking. Invoices sent via WhatsApp with PDF.",
      result: "Zero disputes. Faster collections.",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: <Truck className="w-8 h-8" />,
      title: "Raw Material Ordering",
      description: "Franchises place digital orders. HQ tracks payment and delivery status.",
      result: "Clean supply chain. No confusion.",
      color: "from-purple-500 to-violet-500"
    },
    {
      icon: <UserCheck className="w-8 h-8" />,
      title: "Staff & Attendance",
      description: "Face-recognition based attendance system. Accurate work hours and discipline.",
      result: "No proxy attendance. Better cost control.",
      color: "from-pink-500 to-rose-500"
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Expense Tracking",
      description: "Daily expense entry with category-wise reports. Track every rupee spent.",
      result: "Control leakage at outlet level.",
      color: "from-indigo-500 to-blue-500"
    },
    {
      icon: <WifiOff className="w-8 h-8" />,
      title: "Offline-First PWA",
      description: "Works even without internet. Auto-syncs when online. Runs on mobile, tablet, and desktop.",
      result: "Business never stops.",
      color: "from-slate-600 to-slate-800"
    },
  ];

  const comparisonData = [
    { feature: "Designed for", normal: "Single stores", fos: "Franchise brands" },
    { feature: "Central control", normal: "No HQ dashboard", fos: "Full HQ control" },
    { feature: "Connectivity", normal: "Online only", fos: "Offline-first" },
    { feature: "Staff management", normal: "Not included", fos: "Attendance & payroll-ready" },
    { feature: "Supply chain", normal: "Not available", fos: "Built-in RM ordering" },
    { feature: "Scalability", normal: "Limited", fos: "Franchise-ready by design" },
  ];

  const pricingPlans = [
    {
      name: "FOS Starter",
      price: "499",
      period: "/ outlet / month",
      description: "Perfect for small franchise outlets",
      features: ["Billing & POS", "Basic Reports", "Thermal Printing", "PDF Invoices", "Email Support"],
      popular: false,
      color: "border-slate-200"
    },
    {
      name: "FOS Growth",
      price: "799",
      period: "/ outlet / month",
      description: "GST + reports + expenses",
      features: ["Everything in Starter", "GST Invoicing", "Expense Tracking", "Advanced Reports", "Ledger Management", "Priority Support"],
      popular: false,
      color: "border-slate-200"
    },
    {
      name: "FOS Pro",
      price: "1,199",
      period: "/ outlet / month",
      description: "Staff + attendance + WhatsApp",
      features: ["Everything in Growth", "Staff Management", "Face Recognition Attendance", "WhatsApp Invoices", "Raw Material Ordering", "HQ Dashboard", "Dedicated Support"],
      popular: true,
      color: "border-amber-500"
    },
    {
      name: "FOS Enterprise",
      price: "1,499+",
      period: "/ outlet / month",
      description: "For large franchise brands",
      features: ["Everything in Pro", "Custom Integrations", "Multi-brand Support", "API Access", "Custom Reports", "On-site Training", "24/7 Support"],
      popular: false,
      color: "border-slate-200"
    },
  ];

  const targetAudience = [
    { icon: <Coffee className="w-6 h-6" />, text: "Tea, coffee & juice chains", included: true },
    { icon: <UtensilsCrossed className="w-6 h-6" />, text: "Food & beverage franchises", included: true },
    { icon: <Store className="w-6 h-6" />, text: "Cloud kitchens with multiple outlets", included: true },
    { icon: <ShoppingBag className="w-6 h-6" />, text: "Retail & service franchises", included: true },
    { icon: <Stethoscope className="w-6 h-6" />, text: "Clinics & healthcare franchises", included: true },
  ];

  const faqs = [
    {
      question: "How long does it take to set up FOS?",
      answer: "Most franchises are up and running within 24-48 hours. Our team handles the complete setup including menu configuration, staff training, and data migration."
    },
    {
      question: "Does FOS work offline?",
      answer: "Yes! FOS is built offline-first. Your franchise can continue billing even without internet. All data syncs automatically when connectivity is restored."
    },
    {
      question: "Can I add more outlets later?",
      answer: "Absolutely. FOS scales with you. Add new outlets anytime with a simple setup process. No complex migrations or data transfers needed."
    },
    {
      question: "What about my existing data?",
      answer: "We help you migrate existing data from Excel, Google Sheets, or other systems. Historical orders, ledger balances, and customer data can be imported."
    },
    {
      question: "Is there a free trial?",
      answer: "We offer a 7-day pilot program where you can test FOS with one outlet before committing. Book a demo to learn more."
    },
  ];

  const howItWorks = [
    {
      step: "01",
      title: "Central HQ Control",
      description: "Create and manage franchises, menus, pricing, raw materials, and reports from one dashboard.",
      icon: <Building2 className="w-10 h-10" />
    },
    {
      step: "02",
      title: "Franchise Operations",
      description: "Each outlet runs billing, ledger accounts, staff attendance, expenses, and ordering through a secure PWA.",
      icon: <Store className="w-10 h-10" />
    },
    {
      step: "03",
      title: "Real-Time Visibility",
      description: "All data syncs to HQ automatically — sales, attendance, expenses, and supply orders.",
      icon: <BarChart3 className="w-10 h-10" />
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900">FOS</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 text-sm font-medium">Features</a>
              <a href="#how-it-works" className="text-slate-600 hover:text-slate-900 text-sm font-medium">How it Works</a>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 text-sm font-medium">Pricing</a>
              <a href="#faq" className="text-slate-600 hover:text-slate-900 text-sm font-medium">FAQ</a>
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setShowDemoForm(true)}
                className="hidden sm:block px-4 py-2 text-sm font-medium text-amber-600 hover:text-amber-700"
              >
                Request Demo
              </button>
              <button className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg text-sm font-medium hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg shadow-amber-500/25">
                Start Free Pilot
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-amber-50/50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full text-amber-700 text-sm font-medium mb-6">
              <Zap className="w-4 h-4" />
              Built for Franchise Businesses
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
              Run Your Entire Franchise Business{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-500">
                From One System
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-3xl mx-auto">
              Billing, staff, inventory, expenses, ledgers, and HQ control — built specifically for franchise businesses, not generic stores.
            </p>
            <p className="text-xl font-semibold text-slate-800 mb-10">
              One HQ. Multiple outlets. Total control.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button 
                onClick={() => setShowDemoForm(true)}
                className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-lg font-semibold hover:from-amber-600 hover:to-orange-600 transition-all shadow-xl shadow-amber-500/30 flex items-center justify-center gap-2"
              >
                <Calendar className="w-5 h-5" />
                Request Free Demo
              </button>
              <button className="w-full sm:w-auto px-8 py-4 bg-white text-slate-800 rounded-xl text-lg font-semibold border-2 border-slate-200 hover:border-amber-500 hover:text-amber-600 transition-all flex items-center justify-center gap-2">
                <Play className="w-5 h-5" />
                Start 7-Day Pilot
              </button>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 mt-10 text-sm text-slate-500">
              <span className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-green-500" />
                HQ Controlled
              </span>
              <span className="flex items-center gap-2">
                <WifiOff className="w-4 h-4 text-blue-500" />
                Offline Ready
              </span>
              <span className="flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-purple-500" />
                Works on Any Device
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Franchise Businesses Don't Fail Because of Sales
            </h2>
            <p className="text-xl text-amber-400 font-medium">
              They Fail Because of Poor Systems
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {painPoints.map((pain, index) => (
              <div key={index} className="flex items-start gap-4 p-6 bg-slate-800/50 rounded-xl border border-slate-700">
                <div className="text-red-400 flex-shrink-0 mt-0.5">
                  {pain.icon}
                </div>
                <p className="text-slate-300">{pain.text}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <div className="inline-block px-8 py-4 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-2xl">
              <p className="text-xl text-white font-semibold">
                FOS fixes this at the <span className="text-amber-400">system level</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Introduction */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full text-amber-700 text-sm font-medium mb-6">
            <Award className="w-4 h-4" />
            The Solution
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
            Meet FOS – Franchise Operating System
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-4">
            FOS is a complete operating system designed for franchise brands to centrally manage billing, staff, expenses, inventory, ledgers, and supply chain — across all outlets.
          </p>
          <p className="text-xl font-semibold text-amber-600">
            Unlike normal billing software, FOS is built for control, scalability, and discipline.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              How FOS Works
            </h2>
            <p className="text-lg text-slate-600">Simple 3-step process to transform your franchise operations</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorks.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-100 h-full">
                  <div className="text-6xl font-bold text-amber-100 mb-4">{step.step}</div>
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center text-white mb-6 -mt-12">
                    {step.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                  <p className="text-slate-600">{step.description}</p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-amber-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Everything a Franchise Needs — In One System
            </h2>
            <p className="text-lg text-slate-600">Built for outcomes, not just features</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="group bg-white rounded-2xl p-8 border border-slate-200 hover:border-amber-300 hover:shadow-xl transition-all duration-300">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 mb-4">{feature.description}</p>
                <div className="flex items-center gap-2 text-green-600 font-medium">
                  <CheckCircle2 className="w-5 h-5" />
                  <span>{feature.result}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Is It For */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Who FOS Is Built For
              </h2>
              <div className="space-y-4 mb-8">
                {targetAudience.map((item, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-green-600">
                      {item.icon}
                    </div>
                    <span className="text-slate-700 font-medium">{item.text}</span>
                    <CheckCircle2 className="w-5 h-5 text-green-500 ml-auto" />
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-900 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                <XCircle className="w-6 h-6 text-red-400" />
                Not For
              </h3>
              <div className="p-6 bg-slate-800 rounded-xl border border-slate-700">
                <p className="text-slate-300 text-lg">
                  Single shop owners looking for free billing apps
                </p>
              </div>
              <p className="text-slate-400 mt-6 text-sm">
                FOS is designed for businesses with multiple outlets that need central control and visibility. If you're running a single store, there are simpler alternatives available.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why FOS Instead of Normal Billing Software?
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="text-left p-4 bg-slate-100 rounded-tl-xl font-semibold text-slate-700">Feature</th>
                  <th className="text-center p-4 bg-slate-100 font-semibold text-slate-500">Normal Billing Apps</th>
                  <th className="text-center p-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-tr-xl font-semibold text-white">FOS</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <tr key={index} className="border-b border-slate-100">
                    <td className="p-4 font-medium text-slate-700">{row.feature}</td>
                    <td className="p-4 text-center text-slate-500">{row.normal}</td>
                    <td className="p-4 text-center bg-amber-50 font-medium text-amber-700">{row.fos}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Simple Pricing. Scales With You.
            </h2>
            <p className="text-lg text-slate-400">Pay per outlet. Upgrade only when you grow.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingPlans.map((plan, index) => (
              <div 
                key={index} 
                className={`relative bg-slate-800 rounded-2xl p-6 border-2 ${plan.popular ? 'border-amber-500' : 'border-slate-700'} ${plan.popular ? 'lg:-mt-4 lg:mb-4' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full text-white text-sm font-medium">
                    Most Popular
                  </div>
                )}
                <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-slate-400 text-sm mb-4">{plan.description}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-white">₹{plan.price}</span>
                  <span className="text-slate-400 text-sm">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-2 text-slate-300 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button 
                  className={`w-full py-3 rounded-xl font-medium transition-all ${
                    plan.popular 
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600' 
                      : 'bg-slate-700 text-white hover:bg-slate-600'
                  }`}
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>
          <p className="text-center text-slate-500 mt-8 text-sm">
            * One-time setup fee applies. Optional add-ons available.
          </p>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
            Built From Real Franchise Experience
          </h2>
          <p className="text-lg text-slate-600 mb-10 max-w-3xl mx-auto">
            FOS is not theoretical software. It is built, tested, and refined inside a real franchise operation — designed for Indian business realities.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-6 bg-slate-50 rounded-2xl">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center text-amber-600 mx-auto mb-4">
                <Award className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Battle-Tested</h3>
              <p className="text-slate-600 text-sm">Built and refined in real franchise operations</p>
            </div>
            <div className="p-6 bg-slate-50 rounded-2xl">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-green-600 mx-auto mb-4">
                <Store className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Multi-Outlet Ready</h3>
              <p className="text-slate-600 text-sm">Used across multiple outlets successfully</p>
            </div>
            <div className="p-6 bg-slate-50 rounded-2xl">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 mx-auto mb-4">
                <Globe className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Indian-First</h3>
              <p className="text-slate-600 text-sm">Designed for Indian franchise realities</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full p-6 text-left flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <span className="font-semibold text-slate-900">{faq.question}</span>
                  {expandedFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-slate-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-500" />
                  )}
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-6 text-slate-600">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-amber-500 to-orange-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Take Control of Your Franchise?
          </h2>
          <p className="text-xl text-amber-100 mb-10">
            Stop managing your business on WhatsApp, Excel, and phone calls. Run it on a system.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button 
              onClick={() => setShowDemoForm(true)}
              className="w-full sm:w-auto px-8 py-4 bg-white text-amber-600 rounded-xl text-lg font-semibold hover:bg-amber-50 transition-all shadow-xl flex items-center justify-center gap-2"
            >
              <Calendar className="w-5 h-5" />
              Book Free Demo
            </button>
            <button className="w-full sm:w-auto px-8 py-4 bg-amber-600 text-white rounded-xl text-lg font-semibold border-2 border-amber-400 hover:bg-amber-700 transition-all flex items-center justify-center gap-2">
              <Phone className="w-5 h-5" />
              Talk to Founder
            </button>
            <button className="w-full sm:w-auto px-8 py-4 bg-transparent text-white rounded-xl text-lg font-semibold border-2 border-white/50 hover:bg-white/10 transition-all flex items-center justify-center gap-2">
              <Play className="w-5 h-5" />
              Start 7-Day Pilot
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold text-white">FOS</span>
                <p className="text-slate-500 text-sm">Franchise Operating System</p>
              </div>
            </div>
            <div className="flex items-center gap-6 text-slate-400 text-sm">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="mailto:support@fos.in" className="hover:text-white transition-colors flex items-center gap-2">
                <Mail className="w-4 h-4" />
                support@fos.in
              </a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-800 text-center text-slate-500 text-sm">
            © {new Date().getFullYear()} FOS – Franchise Operating System. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Demo Form Modal */}
      {showDemoForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-slate-900">Request a Demo</h3>
              <button 
                onClick={() => setShowDemoForm(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                <input 
                  type="text" 
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="Enter your name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Business Name</label>
                <input 
                  type="text" 
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="Your franchise/brand name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                <input 
                  type="tel" 
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="+91 98765 43210"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input 
                  type="email" 
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Number of Outlets</label>
                <select className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent">
                  <option value="">Select</option>
                  <option value="1-5">1-5 outlets</option>
                  <option value="6-15">6-15 outlets</option>
                  <option value="16-50">16-50 outlets</option>
                  <option value="50+">50+ outlets</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Message (Optional)</label>
                <textarea 
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                  rows={3}
                  placeholder="Tell us about your requirements..."
                />
              </div>
              <button 
                type="submit"
                className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold hover:from-amber-600 hover:to-orange-600 transition-all"
              >
                Request Demo
              </button>
            </form>
            <p className="text-center text-slate-500 text-sm mt-4">
              We'll contact you within 24 hours
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FOSLandingPage;
